/**
 * Обёртка над MAX WebApp SDK.
 *
 * В боевом окружении MAX инжектит глобальный объект с initData —
 * мы поддерживаем несколько кандидатов на имя, потому что точная API
 * ещё может расходиться между клиентами / версиями.
 *
 * В dev-режиме initData и bot_id можно передать через query-string,
 * например: /u/?dev_bot_id=maxbot1&dev_init_data=...
 */

import { useEffect, useState } from 'react';

export interface WebAppContext {
  botId: string;
  botName: string;
  initData: string;
  isDev: boolean;
  /** Локальный dev: API без initData из бота (см. USER_DEV_BYPASS в .env). */
  devBypass?: boolean;
  devBypassSecret?: string;
}

interface MaxBackButton {
  show?: () => void;
  hide?: () => void;
  onClick?: (handler: () => void) => void;
}

interface MaybeMaxGlobal {
  initData?: string;
  init_data?: string;
  platform?: string;
  device?: { platform?: string };
  close?: () => void;
  BackButton?: MaxBackButton;
  initDataUnsafe?: {
    start_param?: string;
    startapp?: string;
    startPayload?: string;
    start_payload?: string;
  };
  WebApp?: {
    initData?: string;
    init_data?: string;
    close?: () => void;
    BackButton?: MaxBackButton;
    initDataUnsafe?: {
      start_param?: string;
      startapp?: string;
      startPayload?: string;
      start_payload?: string;
    };
    ready?: () => void;
  };
  ready?: () => void;
}

type MaxWindow = Window & {
  maxApp?: MaybeMaxGlobal;
  MaxApp?: MaybeMaxGlobal;
  MaxSDK?: MaybeMaxGlobal;
  Max?: MaybeMaxGlobal;
  MAX?: MaybeMaxGlobal;
  max?: MaybeMaxGlobal;
  WebApp?: MaybeMaxGlobal;
  Telegram?: { WebApp?: MaybeMaxGlobal };
};

const WEB_APP_DATA_KEYS = ['WebAppData', 'webAppData', 'webappdata', 'tgWebAppData'] as const;

const BOT_ID_SLUG_RE = /^[a-z][a-z0-9_]{2,31}$/;
const TICKET_START_RE = /^ticket_(\d+)$/i;
const BOT_TICKET_START_RE = /^([a-z][a-z0-9_]{2,31})_ticket_(\d+)$/i;

/** Парсит startapp / start_param из deep link MAX. */
export function parseStartAppPayload(raw: string): { botId: string; ticketId: string | null } {
  const s = raw.trim();
  if (!s) return { botId: '', ticketId: null };

  const composite = BOT_TICKET_START_RE.exec(s);
  if (composite) return { botId: composite[1]!, ticketId: composite[2]! };

  const ticketOnly = TICKET_START_RE.exec(s);
  if (ticketOnly) return { botId: '', ticketId: ticketOnly[1]! };

  if (BOT_ID_SLUG_RE.test(s)) return { botId: s, ticketId: null };

  return { botId: '', ticketId: null };
}

/** ticket_id из startapp (push) или legacy query ?ticket_id= */
export function readTicketDeepLinkId(): string | null {
  const fromUrl = readFromUrl();
  if (fromUrl.ticketId) return fromUrl.ticketId;

  const fromHash = readFromHash();
  if (fromHash.ticketId) return fromHash.ticketId;

  const startRaw = readStartPayloadFromGlobals();
  const parsedStart = parseStartAppPayload(startRaw);
  if (parsedStart.ticketId) return parsedStart.ticketId;

  return null;
}

function decodeInitDataValue(value: string): string {
  try {
    return decodeURIComponent(value.replace(/\+/g, '%20'));
  } catch {
    return value;
  }
}

/** Разворачивает вложенные WebAppData=… и повторное url-encoding (как в max-ai-sub-bot). */
function collectInitDataVariants(raw: string): string[] {
  const variants = new Set<string>();
  const push = (value: string | null | undefined) => {
    const normalized = String(value ?? '').trim();
    if (normalized) variants.add(normalized);
  };

  push(raw);
  push(decodeInitDataValue(raw));
  push(decodeInitDataValue(decodeInitDataValue(raw)));

  for (const variant of [...variants]) {
    const withoutQuestion = variant.startsWith('?') ? variant.slice(1) : variant;
    if (!withoutQuestion.includes('=')) continue;

    try {
      const params = new URLSearchParams(withoutQuestion);
      for (const key of WEB_APP_DATA_KEYS) {
        const nested = params.get(key);
        if (!nested) continue;
        push(nested);
        push(decodeInitDataValue(nested));
        push(decodeInitDataValue(decodeInitDataValue(nested)));
      }
    } catch {
      // no-op
    }
  }

  return [...variants];
}

function isSignedInitData(value: string): boolean {
  const raw = String(value || '').trim();
  if (!raw.includes('hash=')) return false;
  try {
    const hash = new URLSearchParams(raw).get('hash') || '';
    // MAX/Telegram: usually 64 hex; accept any non-trivial hash so we don't block /api/me.
    return hash.length >= 16;
  } catch {
    return false;
  }
}

function pickInitDataString(candidates: string[]): string {
  for (const candidate of candidates) {
    for (const variant of collectInitDataVariants(candidate)) {
      if (isSignedInitData(variant)) return variant;
    }
  }
  return '';
}

function getMaxWindow(): MaxWindow {
  return window as MaxWindow;
}

function primaryWebApp(): MaybeMaxGlobal | undefined {
  const w = getMaxWindow();
  return w.WebApp ?? w.MAX?.WebApp ?? w.Max?.WebApp ?? w.Telegram?.WebApp ?? undefined;
}

function readStartParamFromUnsafe(webApp?: MaybeMaxGlobal): string {
  const unsafe = webApp?.initDataUnsafe;
  if (!unsafe) return '';
  for (const key of ['start_param', 'startapp', 'startPayload', 'start_payload'] as const) {
    const v = unsafe[key];
    if (typeof v === 'string' && v.trim()) return v.trim();
  }
  return '';
}

function readStartPayloadFromGlobals(): string {
  const w = getMaxWindow();
  const official = readStartParamFromUnsafe(w.WebApp);
  if (official) return official;

  const candidates: Array<MaybeMaxGlobal | undefined> = [
    primaryWebApp(),
    w.WebApp,
    w.maxApp,
    w.MaxApp,
    w.MaxSDK,
    w.Max,
    w.max,
    w.MAX,
    w.MAX?.WebApp,
    w.Max?.WebApp,
    w.Telegram?.WebApp,
  ];
  for (const c of candidates) {
    const unsafe = c?.initDataUnsafe ?? c?.WebApp?.initDataUnsafe;
    if (!unsafe) continue;
    for (const key of ['start_param', 'startapp', 'startPayload', 'start_payload'] as const) {
      const v = unsafe[key];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }

  const unsafeRoots = [w.MAX?.initDataUnsafe, w.max?.initDataUnsafe, w.Max?.initDataUnsafe];
  for (const unsafe of unsafeRoots) {
    if (!unsafe) continue;
    for (const key of ['start_param', 'startapp', 'startPayload', 'start_payload'] as const) {
      const v = unsafe[key];
      if (typeof v === 'string' && v.trim()) return v.trim();
    }
  }

  const initDataRaw = readFromGlobals();
  if (initDataRaw) {
    try {
      const params = new URLSearchParams(initDataRaw);
      for (const key of ['start_param', 'startapp', 'start_payload']) {
        const v = params.get(key);
        if (v?.trim()) return v.trim();
      }
    } catch {
      // no-op
    }
  }
  return '';
}

function readFromGlobals(): string {
  const w = getMaxWindow();
  const webApp = w.WebApp ?? primaryWebApp();
  try {
    webApp?.ready?.();
    w.WebApp?.ready?.();
    w.MAX?.WebApp?.ready?.();
  } catch {
    // no-op
  }

  // Официальный MAX API: window.WebApp.initData (URL-encoded query string).
  if (typeof w.WebApp?.initData === 'string' && w.WebApp.initData.trim()) {
    return w.WebApp.initData.trim();
  }

  const candidates: string[] = [];
  const pushGlobal = (g?: MaybeMaxGlobal) => {
    if (!g) return;
    if (typeof g.initData === 'string') candidates.push(g.initData);
    if (typeof g.init_data === 'string') candidates.push(g.init_data);
    if (typeof g.WebApp?.initData === 'string') candidates.push(g.WebApp.initData);
    if (typeof g.WebApp?.init_data === 'string') candidates.push(g.WebApp.init_data);
  };

  pushGlobal(webApp);
  pushGlobal(w.WebApp);
  pushGlobal(w.MAX);
  pushGlobal(w.max);
  pushGlobal(w.Max);
  pushGlobal(w.maxApp);
  pushGlobal(w.MaxApp);
  pushGlobal(w.MaxSDK);
  pushGlobal(w.Telegram?.WebApp);

  return pickInitDataString(candidates);
}

function readFromUrl(): {
  botId: string;
  botName: string;
  initData: string;
  ticketId: string | null;
} {
  const params = new URLSearchParams(window.location.search);
  const startappRaw =
    params.get('startapp') ??
    params.get('WebAppStartParam') ??
    params.get('webapp_start_param') ??
    params.get('start_param') ??
    '';
  const parsedStart = parseStartAppPayload(startappRaw);
  const botId =
    params.get('bot_id')?.trim() ||
    params.get('botId')?.trim() ||
    parsedStart.botId ||
    params.get('dev_bot_id')?.trim() ||
    '';
  const botName =
    params.get('bot_name') ??
    params.get('botName') ??
    params.get('dev_bot_name') ??
    '';
  const initData = pickInitDataString([
    params.get('init_data') ?? '',
    params.get('initData') ?? '',
    params.get('tgWebAppData') ?? '',
    params.get('webapp_data') ?? '',
    params.get('dev_init_data') ?? '',
  ]);
  const ticketFromInitData = (() => {
    if (!initData) return null;
    try {
      const initParams = new URLSearchParams(initData);
      const startRaw = (
        initParams.get('start_param') ??
        initParams.get('startapp') ??
        initParams.get('start_payload') ??
        ''
      ).trim();
      return parseStartAppPayload(startRaw).ticketId;
    } catch {
      return null;
    }
  })();
  const ticketId =
    parsedStart.ticketId ??
    ticketFromInitData ??
    (() => {
      const q = params.get('ticket_id') ?? params.get('ticketId') ?? '';
      return /^\d+$/.test(q.trim()) ? q.trim() : null;
    })();
  return { botId, botName, initData, ticketId };
}

function readFromHash(): { botId: string; initData: string; ticketId: string | null } {
  const raw = window.location.hash.startsWith('#')
    ? window.location.hash.slice(1)
    : window.location.hash;
  const params = new URLSearchParams(raw);
  const webAppStartParam =
    params.get('WebAppStartParam') ??
    params.get('webapp_start_param') ??
    params.get('start_param') ??
    params.get('startapp') ??
    '';

  const initData = pickInitDataString([
    raw,
    ...WEB_APP_DATA_KEYS.map((key) => params.get(key) ?? ''),
  ]);

  const startParamFromInitData = (() => {
    if (!initData) return '';
    try {
      const initParams = new URLSearchParams(initData);
      return (
        initParams.get('start_param') ??
        initParams.get('startapp') ??
        initParams.get('start_payload') ??
        ''
      ).trim();
    } catch {
      return '';
    }
  })();

  const startRaw = startParamFromInitData || webAppStartParam.trim();
  const parsed = parseStartAppPayload(startRaw);
  return { botId: parsed.botId, initData, ticketId: parsed.ticketId };
}

function readDevBypassConfig(): { enabled: boolean; botId: string; secret: string } {
  const enabled = import.meta.env.VITE_USER_DEV_BYPASS === 'true';
  const botId = String(import.meta.env.VITE_USER_DEV_BOT_ID ?? 'maxbot1');
  const secret = String(import.meta.env.VITE_USER_DEV_BYPASS_SECRET ?? '');
  return { enabled, botId, secret };
}

let cached: WebAppContext | null = null;

const LAUNCHER_INIT_STORAGE_KEY = 'nb_launcher_init_data';
const LAUNCHER_BOT_STORAGE_KEY = 'nb_launcher_bot_id';
const SUPPORT_AUTH_HANDOFF_PREFIX = 'nb_support_auth_v1:';

type LauncherAuthMemory = { initData?: string; botId?: string };

function readLauncherMemory(): LauncherAuthMemory {
  try {
    return ((window as unknown as { __NB_LAUNCHER_AUTH__?: LauncherAuthMemory }).__NB_LAUNCHER_AUTH__ ??
      {}) as LauncherAuthMemory;
  } catch {
    return {};
  }
}

function writeLauncherMemory(initData: string, botId: string): void {
  try {
    const prev = readLauncherMemory();
    (window as unknown as { __NB_LAUNCHER_AUTH__: LauncherAuthMemory }).__NB_LAUNCHER_AUTH__ = {
      initData: initData.includes('hash=') ? initData : prev.initData || '',
      botId: botId && botId !== 'support' ? botId : prev.botId || '',
    };
  } catch {
    // ignore
  }
}

function readLauncherInitFromSession(): string {
  try {
    const value = sessionStorage.getItem(LAUNCHER_INIT_STORAGE_KEY) || '';
    return value.includes('hash=') ? value : '';
  } catch {
    return '';
  }
}

function readLauncherBotFromSession(): string {
  try {
    const value = (sessionStorage.getItem(LAUNCHER_BOT_STORAGE_KEY) || '').trim();
    return value && value !== 'support' ? value : '';
  } catch {
    return '';
  }
}

function consumeSupportAuthHandoffFromWindowName(): { initData: string; botId: string } {
  try {
    const name = String(window.name || '');
    if (!name.startsWith(SUPPORT_AUTH_HANDOFF_PREFIX)) return { initData: '', botId: '' };
    const parsed = JSON.parse(name.slice(SUPPORT_AUTH_HANDOFF_PREFIX.length)) as {
      initData?: string;
      botId?: string;
    };
    window.name = '';
    const initData = String(parsed?.initData || '').trim();
    const botId = String(parsed?.botId || '').trim();
    return {
      initData: initData.includes('hash=') ? initData : '',
      botId: botId && botId !== 'support' ? botId : '',
    };
  } catch {
    return { initData: '', botId: '' };
  }
}

function persistLauncherAuth(initData: string, botId: string): void {
  writeLauncherMemory(initData, botId);
  try {
    if (initData.includes('hash=')) sessionStorage.setItem(LAUNCHER_INIT_STORAGE_KEY, initData);
    if (botId && botId !== 'support') sessionStorage.setItem(LAUNCHER_BOT_STORAGE_KEY, botId);
  } catch {
    // ignore quota / private mode / credentialless iframe
  }
}

/** Short-lived token from launcher: `?h=` / `?handoff=` (not raw initData). */
export function peekHandoffId(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    return (params.get('h') ?? params.get('handoff') ?? '').trim();
  } catch {
    return '';
  }
}

function stripHandoffIdFromUrl(): void {
  try {
    const url = new URL(window.location.href);
    if (!url.searchParams.has('h') && !url.searchParams.has('handoff')) return;
    url.searchParams.delete('h');
    url.searchParams.delete('handoff');
    const next = `${url.pathname}${url.search}${url.hash}`;
    window.history.replaceState({}, '', next);
  } catch {
    // ignore
  }
}

type HandoffExchangeResult = { initData: string; botId: string };

/** Dedupe Strict Mode / remount: one-shot exchange must not fire twice. */
const handoffExchangeInflight = new Map<string, Promise<HandoffExchangeResult>>();
const handoffExchangeDone = new Map<string, HandoffExchangeResult>();

async function exchangeAuthHandoffOnce(handoffId: string): Promise<HandoffExchangeResult> {
  const res = await fetch('/api/auth/handoff/exchange', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ handoff_id: handoffId }),
    credentials: 'include',
  });
  if (!res.ok) {
    let message = `HTTP ${res.status}`;
    try {
      const payload = (await res.json()) as {
        message?: string;
        error?: { message?: string };
      };
      message = payload.error?.message ?? payload.message ?? message;
    } catch {
      // ignore
    }
    throw new Error(message);
  }
  const body = (await res.json()) as { init_data?: string; bot_id?: string };
  const initData = String(body.init_data || '').trim();
  const botId = String(body.bot_id || '').trim();
  if (!isSignedInitData(initData)) {
    throw new Error('handoff_exchange_invalid_init_data');
  }
  return { initData, botId: botId && botId !== 'support' ? botId : '' };
}

/**
 * Exchange one-shot handoff_id → signed initData on Support origin.
 * Does not send X-Max-Init-Data (token is the credential).
 */
export async function exchangeAuthHandoff(handoffId: string): Promise<HandoffExchangeResult> {
  const cached = handoffExchangeDone.get(handoffId);
  if (cached) return cached;

  const inflight = handoffExchangeInflight.get(handoffId);
  if (inflight) return inflight;

  const pending = exchangeAuthHandoffOnce(handoffId)
    .then((result) => {
      handoffExchangeDone.set(handoffId, result);
      return result;
    })
    .finally(() => {
      handoffExchangeInflight.delete(handoffId);
    });
  handoffExchangeInflight.set(handoffId, pending);
  return pending;
}

/** Signed initData from launcher handoff / memory / legacy — independent of Bridge. */
export function peekLauncherInitData(): string {
  // Prefer memory/session from completed exchange or early postMessage.
  const fromMemory = String(readLauncherMemory().initData || '').trim();
  if (isSignedInitData(fromMemory)) return fromMemory;

  const fromSession = readLauncherInitFromSession();
  if (isSignedInitData(fromSession)) return fromSession;

  // With ?h= do not trust query init_data (often truncated by MAX WebView).
  if (peekHandoffId()) return '';

  const fromName = consumeSupportAuthHandoffFromWindowName();
  if (fromName.initData || fromName.botId) persistLauncherAuth(fromName.initData, fromName.botId);
  if (isSignedInitData(fromName.initData)) return fromName.initData;

  try {
    const params = new URLSearchParams(window.location.search);
    for (const key of ['init_data', 'initData', 'tgWebAppData', 'webapp_data'] as const) {
      const value = params.get(key);
      if (value && isSignedInitData(value)) {
        persistLauncherAuth(value.trim(), peekLauncherBotId());
        return value.trim();
      }
    }

    const rawHash = window.location.hash.startsWith('#')
      ? window.location.hash.slice(1)
      : window.location.hash;
    if (rawHash) {
      const hashParams = new URLSearchParams(rawHash);
      for (const key of WEB_APP_DATA_KEYS) {
        const value = hashParams.get(key);
        if (value && isSignedInitData(value)) {
          persistLauncherAuth(value.trim(), peekLauncherBotId());
          return value.trim();
        }
      }
    }
  } catch {
    // ignore
  }

  return '';
}

export function peekLauncherBotId(): string {
  try {
    const params = new URLSearchParams(window.location.search);
    const fromQuery = (params.get('bot_id') ?? params.get('botId') ?? '').trim();
    if (fromQuery && fromQuery !== 'support') {
      persistLauncherAuth(readLauncherInitFromSession() || String(readLauncherMemory().initData || ''), fromQuery);
      return fromQuery;
    }
  } catch {
    // ignore
  }

  const fromMemory = String(readLauncherMemory().botId || '').trim();
  if (fromMemory && fromMemory !== 'support') return fromMemory;

  const fromSession = readLauncherBotFromSession();
  if (fromSession) return fromSession;

  const fromName = consumeSupportAuthHandoffFromWindowName();
  if (fromName.initData || fromName.botId) persistLauncherAuth(fromName.initData, fromName.botId);
  return fromName.botId;
}

function buildWebAppContext(): WebAppContext {
  const fromUrl = readFromUrl();
  const fromHash = readFromHash();
  const startPayload = readStartPayloadFromGlobals();
  const parsedStart = parseStartAppPayload(startPayload);
  // Deep-link payload "support" is a launcher intent, not a support_bots.bot_id.
  const startBotId = parsedStart.botId === 'support' ? '' : parsedStart.botId;
  const initDataGlobal = peekHandoffId() ? '' : readFromGlobals();
  // Prefer handoff (name/postMessage/memory) over query — MAX often truncates long iframe URLs.
  const fromHandoff = peekLauncherInitData();
  const fromLocation = fromHandoff || fromUrl.initData || fromHash.initData;
  const initData = pickPreferredInitData(fromLocation, initDataGlobal);
  const botId =
    fromUrl.botId ||
    peekLauncherBotId() ||
    (fromHash.botId === 'support' ? '' : fromHash.botId) ||
    startBotId ||
    (parsedStart.ticketId ? '' : startPayload === 'support' ? '' : startPayload);
  const isDev = import.meta.env.DEV && !initDataGlobal && !fromLocation;
  const devCfg = readDevBypassConfig();

  if (devCfg.enabled && devCfg.secret && !initData && !fromUrl.botId) {
    return {
      botId: devCfg.botId,
      botName: '',
      initData: '',
      isDev: true,
      devBypass: true,
      devBypassSecret: devCfg.secret,
    };
  }

  return { botId, botName: fromUrl.botName, initData, isDev };
}

/** Prefer a signed payload (contains hash=) from launcher URL over empty Bridge initData. */
function pickPreferredInitData(fromLocation: string, fromBridge: string): string {
  const loc = String(fromLocation || '').trim();
  const bridge = String(fromBridge || '').trim();
  if (isSignedInitData(loc)) return loc;
  if (isSignedInitData(bridge)) return bridge;
  return '';
}

function isContextStable(ctx: WebAppContext): boolean {
  return Boolean(isSignedInitData(ctx.initData) || ctx.devBypass);
}

export function getWebAppContext(): WebAppContext {
  const ctx = buildWebAppContext();
  // Always refresh from URL when it has signed initData (redirect from command-picker).
  if (isContextStable(ctx)) {
    cached = ctx;
    return ctx;
  }
  if (cached && isContextStable(cached)) return cached;
  cached = ctx;
  return ctx;
}

export function isAuthReady(ctx: WebAppContext): boolean {
  if (ctx.devBypass && ctx.botId && ctx.devBypassSecret) return true;
  return isSignedInitData(ctx.initData);
}

/** MAX Bridge for close / BackButton (parent frame when opened via launcher iframe). */
export function getMaxBridge(): MaybeMaxGlobal | undefined {
  const w = getMaxWindow();
  const candidates: Array<MaybeMaxGlobal | undefined> = [
    w.WebApp,
    primaryWebApp(),
    w.MAX?.WebApp,
    w.Max?.WebApp,
    w.Telegram?.WebApp,
    w.maxApp,
    w.MaxApp,
  ];

  try {
    if (window.parent && window.parent !== window) {
      const parent = window.parent as MaxWindow;
      candidates.push(parent.WebApp, parent.MAX?.WebApp, parent.Telegram?.WebApp);
    }
  } catch {
    // cross-origin parent
  }

  for (const bridge of candidates) {
    if (!bridge) continue;
    if (typeof bridge.close === 'function' || bridge.BackButton) return bridge;
  }
  return undefined;
}

export function closeMaxWebApp(): void {
  const bridge = getMaxBridge();
  if (typeof bridge?.close === 'function') {
    try {
      bridge.close();
      return;
    } catch {
      // try fallbacks below
    }
  }

  try {
    if (window.history.length > 1) {
      window.history.back();
      return;
    }
  } catch {
    // ignore
  }
}

export function signalMaxReady(): void {
  try {
    getMaxBridge()?.ready?.();
  } catch {
    // ignore unsupported bridge methods
  }
}

const AUTH_POLL_MS = [0, 50, 100, 200, 400, 800, 1200, 2000, 3000, 5000, 8000];

type SupportAuthMessage = {
  type?: string;
  initData?: string;
  botId?: string;
};

type HandoffUiStatus = 'none' | 'pending' | 'ok' | 'error';

function applySupportAuthMessage(data: SupportAuthMessage | null | undefined): void {
  const initData = String(data?.initData || '').trim();
  const botId = String(data?.botId || '').trim();
  if (!isSignedInitData(initData)) return;
  persistLauncherAuth(initData, botId);
  cached = null;
}

// Capture parent postMessage BEFORE React mounts — picker retries early; late listeners miss auth.
if (typeof window !== 'undefined') {
  window.addEventListener('message', (event: MessageEvent) => {
    try {
      const data = event.data as SupportAuthMessage;
      if (!data || data.type !== 'nb_support_auth') return;
      applySupportAuthMessage(data);
    } catch {
      // ignore
    }
  });
}

/**
 * Ждёт auth: сначала short-lived `?h=` exchange, иначе Bridge / legacy handoff.
 * Не крутит «подключение» бесконечно — после poll или ошибки handoff отдаёт authError.
 */
export function useWebAppContext(): {
  ctx: WebAppContext;
  authReady: boolean;
  waitingForMax: boolean;
  authError: string | null;
  /** True while exchanging `?h=` — do not call /api/me yet. */
  handoffPending: boolean;
} {
  const [ctx, setCtx] = useState(() => getWebAppContext());
  const [handoffStatus, setHandoffStatus] = useState<HandoffUiStatus>(() =>
    peekHandoffId() ? 'pending' : 'none',
  );
  const [authTimedOut, setAuthTimedOut] = useState(false);
  const [authError, setAuthError] = useState<string | null>(null);

  const authReady = isAuthReady(ctx);
  const handoffPending = handoffStatus === 'pending';
  const waitingForMax =
    !authReady &&
    !ctx.devBypass &&
    !authTimedOut &&
    handoffStatus !== 'error' &&
    (handoffStatus === 'pending' || handoffStatus === 'none');

  // Short-lived handoff token from launcher — do not wait for Bridge.
  useEffect(() => {
    const handoffId = peekHandoffId();
    if (!handoffId) return;

    let cancelled = false;
    setHandoffStatus('pending');
    setAuthError(null);

    void (async () => {
      try {
        const exchanged = await exchangeAuthHandoff(handoffId);
        // Persist before cancelled check — React Strict Mode remounts and would
        // otherwise consume the one-shot token then discard without saving auth.
        persistLauncherAuth(exchanged.initData, exchanged.botId);
        stripHandoffIdFromUrl();
        cached = null;
        const next = buildWebAppContext();
        cached = next;
        if (cancelled) return;
        setCtx(next);
        setHandoffStatus('ok');
      } catch (err) {
        // Sibling effect may have already exchanged (deduped) or persisted auth.
        const recovered = buildWebAppContext();
        if (isAuthReady(recovered) || isSignedInitData(peekLauncherInitData())) {
          cached = recovered;
          if (!cancelled) {
            setCtx(recovered);
            setHandoffStatus('ok');
            setAuthError(null);
          }
          return;
        }
        if (cancelled) return;
        setHandoffStatus('error');
        const detail = err instanceof Error && err.message ? ` (${err.message})` : '';
        setAuthError(
          `Не удалось получить данные входа${detail}. Откройте поддержку снова из бота.`,
        );
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  // Bridge / legacy poll — skip when exchanging `?h=`.
  useEffect(() => {
    if (handoffStatus === 'pending' || handoffStatus === 'error') return;
    if (!waitingForMax) return;

    const onMessage = (event: MessageEvent) => {
      try {
        const data = event.data as SupportAuthMessage;
        if (!data || data.type !== 'nb_support_auth') return;
        applySupportAuthMessage(data);
        const next = buildWebAppContext();
        cached = next;
        setCtx(next);
      } catch {
        // ignore malformed postMessage payloads
      }
    };

    window.addEventListener('message', onMessage);

    let cancelled = false;
    let timer: ReturnType<typeof setTimeout> | undefined;

    const step = (index: number) => {
      if (cancelled) return;
      const next = buildWebAppContext();
      cached = next;
      setCtx(next);
      if (isAuthReady(next)) return;
      if (index >= AUTH_POLL_MS.length - 1) {
        setAuthTimedOut(true);
        setAuthError('Нет данных входа от MAX. Откройте поддержку снова из бота.');
        return;
      }
      timer = setTimeout(() => step(index + 1), AUTH_POLL_MS[index + 1]!);
    };

    timer = setTimeout(() => step(0), AUTH_POLL_MS[0]!);
    return () => {
      cancelled = true;
      window.removeEventListener('message', onMessage);
      if (timer) clearTimeout(timer);
    };
  }, [waitingForMax, handoffStatus]);

  return { ctx, authReady, waitingForMax, authError, handoffPending };
}
